import React from 'react'
import ReactSlider from 'react-slider'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import Text from '../../components/atoms/Text/Text'
import dragIconWithoutArrow from '../../assets/images/drag-icon-hexa.png'
import dragIconArrowLeft from '../../assets/images/dotted-arrow-left.png'
import dragIconArrowRight from '../../assets/images/dotted-arrow-right.png'

interface PerceptionBarProps {
  value: number
  onChange: (value: number) => void
}

export const stringHarmless: string = 'HARMLESS'
export const stringNeutral: string = 'NEUTRAL'
export const stringLethal: string = 'LETHAL'

const PerceptionBar: React.FC<PerceptionBarProps> = ({ onChange, value }) => {
  const keyboardControls = useKeyboardControls()

  const sliderId = 'perception-slider'

  const handleChange = (value: number): void => {
    onChange(value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    let newValue = value
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      // Decrease value
      event.preventDefault()
      if (value > 0) {
        newValue = value - 1
      }
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      // Increase value
      event.preventDefault()
      if (value < 100) {
        newValue = value + 1
      }
    }

    if (newValue !== value) {
      handleChange(newValue)
    }
  }

  const Thumb = (props: any): JSX.Element => {
    const isArrowVisible = props?.['aria-valuenow'] === 50
    const { key, ...rest } = props

    return (
      <div
        key={key}
        {...rest}
        className='h-[64px] flex items-center cursor-grab outline-none justify-center relative'
      >
        <div className='relative flex justify-center items-center'>
          {isArrowVisible && (
            <img
              src={dragIconArrowLeft}
              alt='drag icon'
              className='absolute right-[20px] w-[35px] -translate-x-[100%]'
            />
          )}
          <ControlHighlight
            active={keyboardControls.isEnabled && keyboardControls.isElementFocused(sliderId)}
            highlightEffect={HighlightEffect.ButtonHexOutline}
          >
            <img src={dragIconWithoutArrow} alt='drag icon' className='px-[2px]' />
          </ControlHighlight>
          {isArrowVisible && (
            <img
              src={dragIconArrowRight}
              alt='drag icon'
              className='absolute left-[20px] w-[35px] translate-x-[100%]'
            />
          )}
        </div>
      </div>
    )
  }

  const Track = (props: any): JSX.Element => (
    <div key={props.key} className='top-0 bottom-0 absolute w-full perceptionBar pointBar' />
  )

  return (
    <div>
      <div className='relative'>
        <KeyboardControl>
          <div
            id={sliderId}
            role='slider'
            aria-label='perception-slider'
            tabIndex={-1}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={value}
            onKeyDown={handleKeyDown}
            className='w-full h-[64px] relative focus:outline-none'
          >
            <ReactSlider
              className='w-full h-full'
              thumbClassName='example-thumb'
              renderTrack={Track}
              renderThumb={Thumb}
              onChange={handleChange}
              value={value}
            />
          </div>
        </KeyboardControl>
        <span className='absolute left-0 -bottom-[60px]'>
          <Text bold text={stringHarmless} className='text-shadow-sm-green' />
        </span>
        <span className='absolute left-[50%] -translate-x-[50%] -bottom-[60px]'>
          <Text bold text={stringNeutral} className='text-shadow-sm-white' />
        </span>
        <span className='absolute right-0 -bottom-[60px]'>
          <Text bold text={stringLethal} className='text-shadow-sm-red' />
        </span>
      </div>
    </div>
  )
}

export default PerceptionBar
